import { useEffect, useState } from "react";
import { Icone } from "@/components/Icone";
import { aplicarTema, lerTemaSalvo, type Tema } from "@/lib/painel/tema";

/**
 * O tema que está na tela agora: o escolhido na chave, ou — se a pessoa nunca
 * mexeu nela — o do aparelho. Enquanto ninguém escolhe, acompanha o aparelho
 * (quem usa o modo escuro automático à noite vê o painel escurecer junto).
 */
export function useTema(): [Tema, () => void] {
  const [tema, setTema] = useState<Tema>("claro");

  useEffect(() => {
    const sistema = window.matchMedia("(prefers-color-scheme: dark)");
    const ler = () => {
      let salvo: Tema | null = null;
      try {
        salvo = lerTemaSalvo();
      } catch {}
      setTema(salvo ?? (sistema.matches ? "escuro" : "claro"));
    };
    ler();
    sistema.addEventListener("change", ler);
    // A outra chave (menu ou Minha conta) avisa por aqui, para as duas andarem juntas.
    window.addEventListener("lummichat-tema", ler);
    return () => {
      sistema.removeEventListener("change", ler);
      window.removeEventListener("lummichat-tema", ler);
    };
  }, []);

  const alternar = () => {
    const novo: Tema = tema === "escuro" ? "claro" : "escuro";
    aplicarTema(novo);
    setTema(novo);
    window.dispatchEvent(new Event("lummichat-tema"));
  };

  return [tema, alternar];
}

/**
 * A chave do tema escuro. Ligada = escuro. A bolinha mostra o sol ou a lua do
 * tema que está na tela, para ninguém precisar adivinhar o que "ligado" quer
 * dizer.
 */
export function ChaveTema({ className, dica }: { className?: string; dica?: boolean }) {
  const [tema, alternar] = useTema();
  const escuro = tema === "escuro";
  return (
    <button
      type="button"
      role="switch"
      aria-checked={escuro}
      aria-label="Tema escuro"
      onClick={alternar}
      className={
        "group/dica relative h-[26px] w-11 shrink-0 rounded-full transition-colors " +
        (escuro
          ? "bg-[var(--color-realce)] shadow-[inset_0_0_0_1px_var(--color-marca)]"
          : "bg-[var(--color-painel)] shadow-[inset_0_0_0_1px_var(--color-borda-forte)]") +
        (className ? " " + className : "")
      }
    >
      <span
        className={
          "absolute top-[3px] left-[3px] flex h-5 w-5 items-center justify-center rounded-full bg-[var(--color-cartao)] shadow-[0_1px_3px_rgba(0,0,0,0.25)] transition-transform duration-200 " +
          (escuro ? "translate-x-[18px] text-[var(--color-marca-forte)]" : "text-[var(--color-suave)]")
        }
      >
        <Icone nome={escuro ? "lua" : "sol"} className="h-3 w-3" />
      </span>
      {dica && <Dica>{escuro ? "Tema escuro ligado" : "Tema escuro desligado"}</Dica>}
    </button>
  );
}

/**
 * O nome que aparece ao lado de um ícone do menu quando o mouse passa por ele
 * (ou quando o Tab chega). O ícone já carrega o mesmo nome em `aria-label`; esta
 * etiqueta é só para os olhos.
 */
export function Dica({ children }: { children: React.ReactNode }) {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute top-1/2 left-[calc(100%+12px)] z-50 -translate-x-1 -translate-y-1/2 rounded-lg bg-[var(--color-tinta)] px-2.5 py-1.5 text-[12.5px] leading-none font-semibold whitespace-nowrap text-[var(--color-fundo)] opacity-0 shadow-flutua transition-[opacity,transform] duration-150 group-hover/dica:translate-x-0 group-hover/dica:opacity-100 group-focus-visible/dica:translate-x-0 group-focus-visible/dica:opacity-100"
    >
      <span className="absolute top-1/2 -left-1 h-2 w-2 -translate-y-1/2 rotate-45 rounded-[1px] bg-[var(--color-tinta)]" />
      <span className="relative">{children}</span>
    </span>
  );
}
